import { useEffect, useRef, useState } from "react";
import { prettifyApiError } from "@/shared/api";
import { ErrorBanner } from "@/shared/ui/ErrorBanner";
import { giornoMese, oggiIso, type Prenotazione, type RitiroMancato, riprenota } from "../api";
import { StrisciaGiorni } from "../StrisciaGiorni";

type Props = {
  mancato: RitiroMancato;
  /** Giorni proponibili (dal backend): il primo è la prima data utile. */
  giorni: string[];
  /** Giorni che hanno già un ritiro attivo per questo magazzino. */
  giorniOccupati: string[];
  onChiudi: () => void;
  onFatto: (p: Prenotazione) => void;
  /** Chiamata fallita: la scheda si rilegge e chiude il dialog se il ritiro non
   *  è più tra i mancati (ordini già liberati, riprovare qui non può riuscire). */
  onErrore: (messaggio: string) => Promise<void>;
};

/** Conferma della riprenotazione: gli ordini lasciati dal corriere, i loro
 *  colli e il giorno. Mai automatica — il WS Ritiri non riconosce i doppioni. */
export function RiprenotaDialog({
  mancato,
  giorni,
  giorniOccupati,
  onChiudi,
  onFatto,
  onErrore,
}: Props) {
  const [scelto, setScelto] = useState(giorni[0] ?? "");
  const [invio, setInvio] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const n = mancato.lasciati.length;
  const colli = mancato.lasciati.reduce((s, o) => s + o.colli, 0);
  // Come «Aggiungi ritiro»: su un giorno già occupato il ritiro nasce aggiuntivo.
  const aggiuntiva = giorniOccupati.includes(scelto);
  const annullaRef = useRef<HTMLButtonElement>(null);
  // Stesso schema di RecapitiDialog: callback e busy letti da ref, così
  // l'effetto resta solo al mount e non ruba il fuoco a ogni render.
  const onChiudiRef = useRef(onChiudi);
  onChiudiRef.current = onChiudi;
  const invioRef = useRef(invio);
  invioRef.current = invio;

  // Esc chiude (non a chiamata in volo); il fuoco parte su «Annulla»: un
  // Invio distratto non prenota un ritiro.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !invioRef.current) onChiudiRef.current();
    };
    window.addEventListener("keydown", onKey);
    annullaRef.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  async function conferma() {
    setInvio(true);
    setErrore(null);
    try {
      onFatto(await riprenota(mancato.prenotazione_id, { data: scelto, aggiuntiva }));
    } catch (e) {
      const messaggio = prettifyApiError(e);
      setErrore(messaggio);
      // Tasti fermi finché la scheda non ha deciso se il dialog resta aperto.
      await onErrore(messaggio);
    } finally {
      setInvio(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="riprenota-titolo"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="w-full max-w-md rounded-lg border border-line bg-card text-ink shadow-popover">
        <header className="border-b border-line px-5 py-4">
          <h2 id="riprenota-titolo" className="font-display text-[17px] font-bold">
            Riprenota il ritiro del {giornoMese(mancato.data_ritiro)}
          </h2>
        </header>
        <div className="flex flex-col gap-3 px-5 py-4 text-[12.5px]">
          <p>
            <b>
              {n} {n === 1 ? "ordine" : "ordini"} · {colli} {colli === 1 ? "collo" : "colli"}
            </b>{" "}
            rimasti in magazzino:{" "}
            <span className="tabular-nums">
              {mancato.lasciati.map((o) => o.reference).join(", ")}
            </span>
          </p>
          <div>
            <p className="mb-1.5 font-semibold">Giorno del nuovo ritiro</p>
            <StrisciaGiorni
              etichetta="Giorno del nuovo ritiro"
              giorni={giorni}
              oggi={oggiIso()}
              valore={scelto}
              onChange={(g) => setScelto(g ?? "")}
              disabled={invio}
            />
          </div>
          {aggiuntiva && (
            <p className="rounded-lg bg-amber-soft px-3 py-2">
              Il {giornoMese(scelto)} c'è già un ritiro: questo sarà aggiuntivo.
            </p>
          )}
          <ErrorBanner message={errore} />
        </div>
        <footer className="flex justify-end gap-2 border-t border-line px-5 py-3">
          <button
            ref={annullaRef}
            type="button"
            onClick={onChiudi}
            disabled={invio}
            className="rounded-lg border border-line px-3.5 py-2 text-[13px] font-bold hover:bg-paper disabled:cursor-not-allowed"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={() => void conferma()}
            disabled={invio || !scelto}
            aria-busy={invio}
            className="rounded-full border border-ink bg-ink px-3.5 py-2 text-[13px] font-bold text-on-ink disabled:cursor-not-allowed"
          >
            {invio ? "Prenotazione…" : "Conferma e prenota"}
          </button>
        </footer>
      </div>
    </div>
  );
}
